import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { ROLE_GUIDES } from "@/content/role-guides";
import { ROLE_GUIDE_LINKS } from "@/content/role-guide-links";
import { breadcrumbSchema, webPageSchema } from "@/utils/schema";
import { canonicalUrl, jsonLdScript, seo } from "@/utils/seo";
import { Button } from "@super-resume-new/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Briefcase, FileText, Wand2 } from "lucide-react";

const TITLE = "Resume Guides by Role — ATS Keywords & Examples | JobCues";
const DESCRIPTION =
  "Role-by-role resume guides for tech: the literal keywords applicant tracking systems match, the parent terms scanners never infer, before and after bullet rewrites, and the section order that fits each role.";

export const Route = createFileRoute("/resume/")({
  head: () => ({
    ...seo({ title: TITLE, description: DESCRIPTION, path: "/resume", type: "article" }),
    scripts: [
      jsonLdScript(
        webPageSchema({ name: TITLE, description: DESCRIPTION, path: "/resume" }),
      ),
      jsonLdScript(
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Resume Guides", path: "/resume" },
        ]),
      ),
      // The hub's own index, so an answer engine sees the full set from one page.
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "JobCues resume guides by role",
        numberOfItems: ROLE_GUIDE_LINKS.length,
        itemListElement: ROLE_GUIDE_LINKS.map((guide, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: `${guide.name} resume`,
          url: canonicalUrl(`/resume/${guide.slug}`),
        })),
      }),
    ],
  }),
  component: ResumeGuidesHub,
});

function ResumeGuidesHub() {
  return (
    <PageLayout className="max-w-5xl">
      <PageHero
        eyebrow="Resume Guides"
        icon={FileText}
        title="Resume guides by role"
        description="One guide per role, written against the postings currently in the JobCues directory. Each one lists the keywords a scanner matches literally, the parent terms it never infers, and three bullets rewritten line by line."
      />

      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-foreground">
          Why a resume needs to change per role
        </h2>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          An applicant tracking system scores a resume by matching the job description's literal
          terms against the document. The terms differ per role, and the scanner infers nothing:
          PostgreSQL does not register as SQL, React does not register as JavaScript, and Figma does
          not register as prototyping. Each guide below is the keyword list and the bullet structure
          for one role, so the tailoring starts from the right baseline.
        </p>
      </div>

      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
        {ROLE_GUIDES.map((guide) => (
          <li key={guide.slug}>
            <Link
              to="/resume/$role"
              params={{ role: guide.slug }}
              className="group flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-brand/40"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-foreground">{guide.name} resume</span>
                <ArrowRight
                  className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </span>
              <span className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {guide.definition.split(". ")[0]}.
              </span>
              <span className="mt-3 text-xs text-muted-foreground">
                {guide.hardKeywords.length} keywords · {guide.bullets.length} bullet rewrites ·{" "}
                {guide.faqs.length} questions
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-10 rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">Skip the general case</h2>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          These lists cover what postings ask for on average. The score that decides your
          application is the one against the specific description you are answering. Paste it and
          get the matched terms, the missing terms, and a rewritten resume in about 15 seconds.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/resume-tailor">
            <Button className="cursor-pointer">
              <Wand2 className="size-4" />
              Tailor my resume
            </Button>
          </Link>
          <Link to="/jobs">
            <Button variant="outline" className="cursor-pointer">
              <Briefcase className="size-4" />
              Browse verified tech jobs
            </Button>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
